import React, { useState, useEffect, useRef } from "react";
import { Table, Button, Modal, Input, DatePicker, Space, ConfigProvider } from "antd";
import { SearchOutlined } from '@ant-design/icons';
import { Image } from "@chakra-ui/image";
import CustomToast from "../../components/CustomToast";
import { exportFlagshipStore, formFlagship } from "../../utils/api/formsApi";
import 'antd/dist/reset.css';
import dayjs from 'dayjs';

const baseUrl = process.env.REACT_APP_LINK

function AwardingFlagshipStore() {
  const [details, setMessage] = useState({ message: "", type: "" });
  const [showToast, setShowToast] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [width, setWidth] = useState(window.innerWidth);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  
  const [searchData, setSearchData] = useState({
    agent: "",
    dateTo: dayjs(),
    dateFrom: dayjs(),
  });
  
  const [show, setShow] = useState(false);
  const [image, setImage] = useState("");
  const imageRef = useRef()
  const [fileName, setFileName] = useState("");
  // Base URL for API
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  const columns = [
    {
      title: <span className="text-white px-2 py-1 block">ID</span>,
      dataIndex: 'id',
      key: 'id',
      width: 80,
      align: 'center',
    },
    {
      title: <span className="text-white px-2 py-1 block">MERCHANT</span>,
      dataIndex: 'merchant',
      key: 'merchant',
      width: 200,
      align: 'center',
    },
    {
      title: <span className="text-white px-2 py-1 block">FLAGSHIP FREEBIES</span>,
      dataIndex: 'flagship_freebies',
      key: 'flagship_freebies',
      width: 150,
      align: 'center',
      render: (url) => url ? <a href="#" onClick={() => handleShow(url, 'Flagship Freebies Certificate')}>View Certificate</a> : '',
    },
    {
      title: <span className="text-white py-1 block">ECO BAG</span>,
      dataIndex: 'total_eco_bag',
      key: 'eco_bag',
      width: 100,
      align: 'center',
    },
    {
      title: <span className="text-white px-2 py-1 block">BALLPEN</span>,
      dataIndex: 'total_ballpen',
      key: 'ballpen',
      width: 100,
      align: 'center',
    },
    {
      title: <span className="text-white px-2 py-1 block">NOTEBOOK</span>,
      dataIndex: 'total_notebook',
      key: 'notebook',
      width: 100,
      align: 'center',
    },
    {
      title: <span className="text-white px-2 py-1 block">FAN</span>,
      dataIndex: 'total_fan',
      key: 'fan',
      width: 80,
      align: 'center',
    },
    {
      title: <span className="text-white px-2 py-1 block">CALCULATOR</span>,
      dataIndex: 'total_calculator',
      key: 'calculator',
      width: 100,
      align: 'center',
    },
    {
      title: <span className="text-white px-2 py-1 block">MUG</span>,
      dataIndex: 'total_mug',
      key: 'mug',
      width: 80,
      align: 'center',
    },
    {
      title: <span className="text-white px-2 py-1 block">RECEIVER NAME</span>,
      dataIndex: 'flagship_receiver_name',
      key: 'receiver_name',
      width: 180,
      align: 'center',
    },
    {
      title: <span className="text-white py-1 block">RECEIVER POSITION</span>,
      dataIndex: 'flagship_receiver_position',
      key: 'receiver_position',
      width: 180,
      align: 'center',
    },
    {
      title: <span className="text-white px-2 py-1 block">SATISFIED</span>,
      dataIndex: 'is_flagship_satisfied',
      key: 'satisfied',
      width: 120,
      align: 'center',
    },
    {
      title: <span className="text-white px-2 py-1 block">FEEDBACK</span>,
      dataIndex: 'flagship_feedback',
      key: 'feedback',
      width: 200,
      align: 'center',
    },
    {
      title: <span className="text-white py-1 block">OPTION PRIZE</span>,
      dataIndex: 'flagship_option_prize',
      key: 'option_prize',
      width: 150,
      align: 'center',
    },
  ];

  const handleClose = () => setShow(false);
  const handleShow = (image, file) => {
    setFileName(file);
    setImage(image);
    setShow(true);
    imageRef.current = `${baseUrl}${image}`;
  };

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

    const handleSearch = async (page = 1, pageSize = 10) => {
    try {
      setLoading(true);
      const response = await formFlagship(
        {
          ...searchData,
          dateFrom: searchData.dateFrom.format('YYYY-MM-DD'),
          dateTo: searchData.dateTo.format('YYYY-MM-DD')
        }, 
        page  // ✅ integer na direkta
      );

      if (response?.data?.data) {
        const formattedData = response.data.data.map(item => ({
          ...item,
          key: item.id,
        }));

        setData(formattedData);
        setPagination(prev => ({
          ...prev,
          current: page,      // ✅ i-update ang current page
          pageSize: pageSize,
          total: response.data.total_forms || 0,
        }));
      }
    } catch (error) {
      const responseMessage = error.response?.data?.messages?.error || 'Something went wrong.';
      setMessage({ message: responseMessage, type: "danger" });
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      await exportFlagshipStore({
        ...searchData,
        dateFrom: searchData.dateFrom.format('YYYY-MM-DD'),
        dateTo: searchData.dateTo.format('YYYY-MM-DD')
      });
      
      setMessage({
        message: 'Export started successfully',
        type: 'success',
      });
      setShowToast(true);
    } catch (error) {
      setMessage({
        message: 'Failed to start export',
        type: 'danger',
      });
      setShowToast(true);
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    handleSearch(pagination);
  };

  useEffect(() => {
    handleSearch(1, 10);
  }, []);

  useEffect(() => {
    handleSearch(pagination);
  }, []);

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#FB9C0C',
          borderRadius: 6,
        },
      }}
    >
    <div className="p-2">
      <div className="text-center">
        <div className="col-12">
          <h1 className="title">AWARDING FLAGSHIP STORE</h1>
        </div>
      </div>
      
      <div className="d-flex flex-wrap gap-3 mb-4 align-items-end">
        <div style={{ width: "250px" }}>
          <label style={{ fontWeight: 500, marginBottom: 4, display: 'block' }}>Date From</label>
          <DatePicker
            style={{ width: '100%' }}
            value={searchData.dateFrom}
            onChange={(date) => setSearchData({ ...searchData, dateFrom: date })}
            format="YYYY-MM-DD"
            allowClear={false}
          />
        </div>
        
        <div style={{ width: "250px" }}>
          <label style={{ fontWeight: 500, marginBottom: 4, display: 'block' }}>Date To</label>
          <DatePicker
            style={{ width: '100%' }}
            value={searchData.dateTo}
            onChange={(date) => setSearchData({ ...searchData, dateTo: date })}
            format="YYYY-MM-DD"
            allowClear={false}
          />
        </div>
        
        <div style={{ width: "250px" }}>
          <label style={{ fontWeight: 500, marginBottom: 4, display: 'block' }}>Agent</label>
          <Input
            placeholder="Search Agent"
            value={searchData.agent}
            onChange={(e) => setSearchData({ ...searchData, agent: e.target.value })}
          />
        </div>
        
        <div className="d-flex align-items-end">
          <Space>
            <Button type="primary" onClick={() => handleSearch(1, pagination.pageSize)}>
              Search
            </Button>
            <Button type="primary" onClick={handleExport} disabled={!data || data.length === 0}>
              Export
            </Button>
          </Space>
        </div>
      </div>
      
      <div className="row">
              <div className="col-12">
                <div style={{ overflowX: 'auto', maxHeight: '70vh', overflowY: 'auto' }}>
                  <Table
                    // className="no-hover"
                    columns={columns}
                    dataSource={data}
                    rowKey="id"
                    pagination={{
                      ...pagination,
                      showSizeChanger: true,
                      pageSizeOptions: ['10', '20', '50', '100'],
                      showTotal: (total) => `Total ${total} items`,
                      onChange: (page, pageSize) => handleSearch(page, pageSize),
                    }}
                    scroll={{ x: 'max-content' }}
                    bordered
                    size="small"
                    loading={loading}
                  />
                </div>
              </div>
            </div>
      
      <CustomToast
        type={details.type}
        message={details.message}
        title="Awarding Flagship Store"
        show={showToast}
        setShow={setShowToast}
      />
      
      <Modal 
        title={fileName} 
        open={show} 
        onCancel={handleClose}
        footer={null}
        centered
      >
        <div className="text-center">
          <Image
            src={imageRef.current}
            // fallbackSrc="../../logo.png"
            alt="Flagship Image"
            style={{ 
              maxWidth: "100%", 
              maxHeight: width > 768 ? "70vh" : "50vh",
              objectFit: 'contain'
            }}
            className="img-fluid mx-auto"
          />
        </div>
      </Modal>
    </div>
    </ConfigProvider>
  );
}

export default AwardingFlagshipStore;
